import React, { useEffect, useState, useCallback } from 'react';
import { Scale, RefreshCw, ExternalLink, Mail, Building2, User, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' });
const PERSO = 'Affaire personnelle / non rattachée à une société';

const couleurStatut = (s = '') => (s.includes('clô') ? 'bg-stone-200 text-stone-700' : s.includes('attente') ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800');

const CarteAffaire = ({ a, actif, onClick }) => (
  <button data-testid={`affaire-carte-${a.id}`} onClick={onClick}
    className={`w-full text-left p-4 rounded-xl border transition-colors duration-200 ${actif ? 'border-emerald-600 bg-emerald-50' : 'border-stone-200 bg-white hover:bg-stone-50'}`}>
    <div className="flex items-start justify-between gap-2">
      <p className="font-semibold text-stone-800 text-sm leading-snug">{a.nom}</p>
      <ChevronRight className="w-4 h-4 text-stone-400 shrink-0" />
    </div>
    <div className="flex flex-wrap items-center gap-2 mt-2">
      <span className={`text-xs px-2 py-0.5 rounded-full ${couleurStatut(a.statut)}`}>{a.statut || 'en cours'}</span>
      <span className="text-xs text-stone-500">{a.nb_mails} mails · {String(a.derniere_activite || '').slice(0, 10)}</span>
    </div>
  </button>
);

const Detail = ({ a }) => (
  <div data-testid="affaire-detail" className="bg-white border border-stone-200 rounded-2xl p-6 space-y-5">
    <div className="flex items-start justify-between gap-4">
      <div>
        <h2 className="text-lg font-bold text-stone-900">{a.nom}</h2>
        <p className="text-sm text-stone-500 mt-1 flex items-center gap-1.5">
          {a.societe_nom === PERSO ? <User className="w-4 h-4" /> : <Building2 className="w-4 h-4" />}{a.societe_nom}
        </p>
      </div>
      {a.drive_url && (
        <a data-testid="affaire-drive-lien" href={a.drive_url} target="_blank" rel="noreferrer"
          className="text-sm text-emerald-700 hover:underline flex items-center gap-1 shrink-0">Drive <ExternalLink className="w-3.5 h-3.5" /></a>
      )}
    </div>
    <div className="grid sm:grid-cols-2 gap-3 text-sm">
      {[['Parties', (a.parties || []).join(', ')], ['Avocat', a.avocat], ['Juridiction', a.juridiction], ['N° dossier', a.numero_dossier], ['Montant en jeu', a.montant_en_jeu], ['Statut', a.statut]]
        .filter(([, v]) => v).map(([k, v]) => (<div key={k}><span className="text-stone-500">{k} : </span><span className="text-stone-800">{v}</span></div>))}
    </div>
    <section><h3 className="text-sm font-semibold text-stone-700 mb-1">Résumé</h3><p className="text-sm text-stone-700 whitespace-pre-line">{a.resume}</p></section>
    <section className="bg-amber-50 border border-amber-200 rounded-xl p-3">
      <h3 className="text-sm font-semibold text-amber-900 mb-1">Prochaine étape</h3>
      <p data-testid="affaire-prochaine-etape" className="text-sm text-amber-900">{a.prochaine_etape || '—'}</p>
    </section>
    {(a.chronologie || []).length > 0 && (
      <section><h3 className="text-sm font-semibold text-stone-700 mb-2">Chronologie</h3>
        <ol className="border-l-2 border-emerald-200 ml-2 space-y-2">
          {a.chronologie.map((c, i) => (<li key={i} className="pl-4 text-sm"><span className="font-medium text-emerald-800">{c.date}</span> – {c.fait}</li>))}
        </ol>
      </section>
    )}
    <section><h3 className="text-sm font-semibold text-stone-700 mb-2">Mails ({a.nb_mails})</h3>
      <div className="space-y-1.5 max-h-80 overflow-y-auto">
        {(a.mails || []).slice().reverse().map((m) => (
          <a key={m.message_id} href={m.lien} target="_blank" rel="noreferrer" data-testid={`affaire-mail-${m.message_id}`}
            className="flex items-start gap-2 p-2 rounded-lg hover:bg-stone-50 text-sm">
            <Mail className="w-4 h-4 text-stone-400 mt-0.5 shrink-0" />
            <span className="min-w-0"><span className="text-stone-500">{m.date.slice(0, 10)} · {m.boite}</span><br />
              <span className="text-stone-800">{m.objet}</span>{m.pj?.length > 0 && <span className="text-stone-500"> · {m.pj.length} PJ</span>}</span>
          </a>
        ))}
      </div>
    </section>
  </div>
);

export default function AffairesJuridiquesPage() {
  const [affaires, setAffaires] = useState([]);
  const [scan, setScan] = useState(null);
  const [choisie, setChoisie] = useState(null);
  const [chargement, setChargement] = useState(true);

  const charger = useCallback(async () => {
    const r = await fetch(`${API}/api/juridique/affaires`, { headers: headers() });
    if (r.ok) { const d = await r.json(); setAffaires(d.affaires || []); setScan(d.scan); }
    setChargement(false);
  }, []);

  useEffect(() => { charger(); }, [charger]);
  useEffect(() => {
    if (scan?.statut !== 'en_cours') return undefined;
    const t = setInterval(charger, 8000);
    return () => clearInterval(t);
  }, [scan, charger]);

  const ouvrir = async (id) => {
    const r = await fetch(`${API}/api/juridique/affaires/${id}`, { headers: headers() });
    if (r.ok) setChoisie(await r.json());
  };

  const lancerScan = async () => {
    const r = await fetch(`${API}/api/juridique/affaires/scanner`, { method: 'POST', headers: headers() });
    if (r.ok) { toast.success('Maître Blanc relit toutes tes boîtes mail, cela prend quelques minutes.'); setScan({ statut: 'en_cours' }); }
  };

  const groupes = affaires.reduce((acc, a) => { (acc[a.societe_nom] = acc[a.societe_nom] || []).push(a); return acc; }, {});
  const ordre = Object.keys(groupes).sort((x, y) => (x === PERSO) - (y === PERSO) || x.localeCompare(y));

  return (
    <div data-testid="affaires-juridiques-page" className="p-6 lg:p-10 max-w-7xl">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-stone-900 flex items-center gap-3"><Scale className="w-8 h-8 text-emerald-700" />Affaires juridiques</h1>
          <p className="text-sm text-stone-500 mt-2">Classées par Maître Blanc à partir de toutes tes boîtes mail, une fiche par affaire, rangées par société.</p>
        </div>
        <Button data-testid="affaires-scan-btn" onClick={lancerScan} disabled={scan?.statut === 'en_cours'} className="rounded-full bg-emerald-700 hover:bg-emerald-800">
          {scan?.statut === 'en_cours' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          {scan?.statut === 'en_cours' ? 'Analyse en cours…' : 'Relire mes boîtes mail'}
        </Button>
      </div>
      {scan?.fin && <p data-testid="affaires-scan-info" className="text-xs text-stone-500 mb-4">Dernière analyse : {scan.fin.slice(0, 16).replace('T', ' ')} · {scan.fils_analyses} fils lus · {scan.nb_affaires} affaires</p>}
      {chargement ? <Loader2 className="w-6 h-6 animate-spin text-stone-400" /> : affaires.length === 0 ? (
        <p data-testid="affaires-vide" className="text-stone-500">Aucune affaire classée pour l'instant. Lance une analyse de tes boîtes mail.</p>
      ) : (
        <div className="grid lg:grid-cols-[minmax(0,380px)_1fr] gap-6 items-start">
          <div className="space-y-6">
            {ordre.map((soc) => (
              <div key={soc}>
                <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2 flex items-center gap-1.5">
                  {soc === PERSO ? <User className="w-3.5 h-3.5" /> : <Building2 className="w-3.5 h-3.5" />}{soc}
                  <Badge variant="outline" className="ml-1">{groupes[soc].length}</Badge>
                </p>
                <div className="space-y-2">{groupes[soc].map((a) => <CarteAffaire key={a.id} a={a} actif={choisie?.id === a.id} onClick={() => ouvrir(a.id)} />)}</div>
              </div>
            ))}
          </div>
          {choisie ? <Detail a={choisie} /> : <p className="text-sm text-stone-500">Choisis une affaire pour voir sa fiche.</p>}
        </div>
      )}
    </div>
  );
}
